import { createRng } from "@/lib/seededRandom";
import type { Employee, OpenRole, RecruitmentFunnelStage } from "@/types";
import { AVATAR_COLORS, DEPARTMENT_NAMES, LOCATIONS, VA_ROLES, fullName } from "./constants";

const EMPLOYEE_SEED = 90210;

const LEADERSHIP_ROLES: Record<string, string[]> = {
  Sales: ["Sales Executive", "Business Development Rep", "Sales Manager"],
  Marketing: ["Marketing Specialist", "SEO Strategist", "Content Marketer", "Marketing Manager"],
  "Client Success": ["Account Manager", "Operations Manager", "Client Success Manager"],
  "Service Delivery": ["Team Leader", "Senior Team Leader", "Delivery Lead"],
  "Talent Acquisition": ["Recruiter", "Sourcing Specialist", "Talent Acquisition Manager"],
  "People & Culture": ["HR Business Partner", "People Ops Specialist", "Culture Lead"],
  Finance: ["Financial Analyst", "AP/AR Specialist", "Controller"],
  "Learning & Development": ["L&D Specialist", "Training Manager", "Instructional Designer"],
  "Technology / AI": ["AI/Automation Engineer", "Platform Engineer", "Data Analyst"],
};

const DEPARTMENT_WEIGHTS: Array<[string, number]> = [
  ["Service Delivery", 300],
  ["Client Success", 42],
  ["Sales", 16],
  ["Marketing", 13],
  ["Talent Acquisition", 16],
  ["People & Culture", 11],
  ["Finance", 13],
  ["Learning & Development", 9],
  ["Technology / AI", 9],
];

const MANAGER_POOL = Array.from({ length: 24 }, (_, i) => `manager-${i}`);

function buildEmployees(): Employee[] {
  const rng = createRng(EMPLOYEE_SEED);
  const managerNames = MANAGER_POOL.map(() => fullName(rng));
  const employees: Employee[] = [];
  let idx = 0;

  for (const [department, count] of DEPARTMENT_WEIGHTS) {
    for (let i = 0; i < count; i++) {
      idx++;
      const isServiceDelivery = department === "Service Delivery";
      // ~14% of Service Delivery heads lead a pod (Team Leader/Senior TL/Delivery Lead);
      // the rest are the VAs those leads manage.
      const isDeliveryLead = isServiceDelivery && rng.bool(0.14);
      const role = isDeliveryLead
        ? rng.pick(LEADERSHIP_ROLES["Service Delivery"])
        : isServiceDelivery
          ? rng.pick(VA_ROLES)
          : rng.pick(LEADERSHIP_ROLES[department] ?? ["Specialist"]);

      const tenureMonths = rng.int(1, 96);
      const hireDate = (() => {
        const d = new Date();
        d.setMonth(d.getMonth() - tenureMonths);
        return d.toISOString();
      })();

      const status = rng.pickWeighted<Employee["status"]>([
        ["active", 88],
        ["bench", 6],
        ["onboarding", 4],
        ["offboarding", 2],
      ]);

      const performanceRating = rng.int(48, 99);
      const riskFlag = performanceRating < 62 || rng.bool(0.04);

      employees.push({
        id: `emp-${idx}`,
        name: fullName(rng),
        avatarColor: rng.pick(AVATAR_COLORS),
        role,
        department,
        status,
        billable: isServiceDelivery ? !isDeliveryLead && status === "active" : rng.bool(0.3),
        managerName: rng.pick(managerNames),
        hireDate,
        performanceRating,
        attendanceScore: rng.int(70, 100),
        trainingCompletion: rng.int(40, 100),
        satisfactionScore: rng.int(55, 98),
        managerSatisfactionScore: rng.int(55, 98),
        riskFlag,
        tenureMonths,
        location: rng.pick(LOCATIONS),
      });
    }
  }

  return employees;
}

export const EMPLOYEES: Employee[] = buildEmployees();

function pool(role: string) {
  return EMPLOYEES.filter((e) => e.role === role && e.status === "active");
}

export const ACCOUNT_MANAGERS = pool("Account Manager");
export const OPERATIONS_MANAGERS = pool("Operations Manager");
export const TEAM_LEADERS = [...pool("Team Leader"), ...pool("Senior Team Leader")];
export const VIRTUAL_ASSISTANTS = EMPLOYEES.filter(
  (e) => e.department === "Service Delivery" && e.status === "active" && (VA_ROLES as readonly string[]).includes(e.role),
);

export const OPEN_ROLES: OpenRole[] = (() => {
  const rng = createRng(EMPLOYEE_SEED + 1);
  const stages: OpenRole["stage"][] = ["sourcing", "screening", "interviewing", "offer", "closing"];
  return Array.from({ length: 22 }, (_, i) => {
    const department = rng.pick(DEPARTMENT_NAMES);
    const roles = LEADERSHIP_ROLES[department] ?? VA_ROLES;
    return {
      id: `role-${i + 1}`,
      title: rng.pick([...roles, ...VA_ROLES]),
      department,
      stage: rng.pick(stages),
      candidatesInPipeline: rng.int(2, 40),
      daysOpen: rng.int(3, 95),
      priority: rng.pickWeighted<OpenRole["priority"]>([
        ["critical", 10],
        ["high", 30],
        ["medium", 40],
        ["low", 20],
      ]),
      hiringManager: rng.pick(EMPLOYEES.filter((e) => e.department === department)).name,
    };
  });
})();

export const RECRUITMENT_FUNNEL: RecruitmentFunnelStage[] = [
  { stage: "Sourced", count: 842 },
  { stage: "Screened", count: 401 },
  { stage: "Interviewing", count: 176 },
  { stage: "Offer Extended", count: 58 },
  { stage: "Hired", count: 41 },
];
